import { BookingServiceClient } from 'fitbeat.api-clients';
import { IDisplayableUserProfile, UserProfile, sleep } from 'fitbeat.gym-core';
import { Platform } from 'react-native';
import createAgoraRtcEngine, {
  AudioProfileType,
  AudioScenarioType,
  AudioSessionOperationRestriction, AudioVolumeInfo, ChannelProfileType, ClientRoleType,
  ConnectionStateType,
  IRtcEngine,
  LocalAudioStreamState,
  LocalVideoStreamState,
  UserInfo,
  VideoDimensions, VideoMirrorModeType,
} from 'react-native-agora';
import { getConfig } from '../../../config';
import build from '../../../config/build';
import { analyicsForAction } from '../../../services/google-analytics/analyicsForAction';
import { Logger } from '../../../utils/Logger';
import { Permissions } from '../../../utils/Permissions';
import { IVolumeChangedAction, beepVolumeChanged, voiceVolumeChanged } from '../../actions';
import { IAppStore } from '../../reducer';
import { selectIsStreamingWorkoutAndNotHost } from '../../selectors';
import { AudioHelper } from '../../utils/AudioHelper';
import { CallConnectionState } from './reducer';

export const CALL_INITIATING = 'CALL_INITIATING';
export const CALL_INITIATE_SUCCESS = 'workout_video_call_enabled';
export const CALL_CONNECTED = 'CALL_CONNECTED';
export const CALL_DISCONNECT_REQUEST = 'CALL_DISCONNECT_REQUEST';
export const CALL_DISCONNECTED = 'workout_video_call_disabled';
export const CALL_FAILED = 'CALL_FAILED';

export const PARTICIPANT_JOINED_CALL = 'PARTICIPANT_AUDIO_VIDEO_TRACK';
export const PARTICIPANT_LEFT_CALL = 'PARTICIPANT_LEFT_CALL';

export const TOGGLE_LOCAL_AUDIO_MUTED = 'TOGGLE_LOCAL_AUDIO_MUTED';
export const TOGGLE_LOCAL_VIDEO_MUTED = 'TOGGLE_LOCAL_VIDEO_MUTED';

export const TALKING_TOGGLE_REQUEST = 'TALKING_TOGGLE_REQUEST';

export const ENABLE_TALKING_SUCCESS = 'workout_video_call_workout_talk_enabled';
export const DISABLE_TALKING_SUCCESS = 'workout_video_call_workout_talk_disabled';

export const CLASS_LIST_LOADED = 'CLASS_LIST_LOADED';

export const USER_TOGGLE_VIDEO = 'USER_TOGGLE_VIDEO';
export const SPEAKER_VOLUME_CHHANGED = 'SPEAKER_VOLUME_CHHANGED';

let agoraEngine: IRtcEngine | null = null;

export const initiateGroupCall = (
  bookingServiceClient: BookingServiceClient,
  translate: (key: string) => string,
  userProfile: UserProfile,
  channelName: string,
) => async (dispatch: (action: VideoCallingActionTypes) => void, getState: () => IAppStore) => {
  try {
    const state = getState();
    const { homeWorkouts } = state;
    const isStreamingWorkoutAndNotHost = selectIsStreamingWorkoutAndNotHost(state);

    if (!homeWorkouts.homeWorkoutSession) {
      return;
    }

    dispatch({
      type: CALL_INITIATING,
      payload: {
        userProfileId: homeWorkouts.userProfile.uid,
        isStreamingWorkoutAndNotHost,
      },
    });

    const response = await bookingServiceClient.getVideoCallAccessToken(channelName);
    const {
      accessToken,
      appId,
      userAccount,
      settings,
    } = response;

    const requiresPermissions = settings.isHost || !settings.isStreamingWorkout;
    if (requiresPermissions) {
      const cameraPermission = await Permissions.requestCameraPermission();
      const microphonePermission = await Permissions.requestMicrophonePermission();
      const hasRequiredPermissions = cameraPermission && microphonePermission;
      if (!hasRequiredPermissions) {
        dispatch({
          type: CALL_FAILED,
          payload: {
            errorMessage: 'Required permission not provided',
            permissionError: true,
          },
        });
        return;
      }
    }
    const config = getConfig();
    await sleep(config.groupWorkoutVideoCallStartDelayInMs);
    await initializeAgoraEngine(
      accessToken,
      appId,
      channelName,
      userAccount,
      settings,
      dispatch,
      userProfile,
      getState(),
    );
    dispatch({
      type: CALL_INITIATE_SUCCESS,
      payload: {
        accessToken,
        settings,
      },
      ...analyicsForAction(CALL_INITIATE_SUCCESS),
    });
  } catch (e) {
    Logger.notify(e);
    dispatch({
      type: CALL_FAILED,
      payload: {
        errorMessage: e.message,
      },
    });
  }
};

interface ICallConnectedAction {
  type: typeof CALL_CONNECTED;
  payload: {
    currentTime: Date;
    callIsConnected: true;
    debug: string;
  }
}

export const callConnected = (): ICallConnectedAction => {
  try {
    return {
      type: CALL_CONNECTED,
      payload: {
        currentTime: new Date(),
        callIsConnected: true,
        debug: 'Updated workout screen on call connected',
      },
    };
  } catch (e) {
    Logger.notify(e);
    throw e;
  }
};

export const callDisconnectRequest = (): any => async (dispatch: (action: any) => void) => {
  try {
    dispatch({
      type: CALL_DISCONNECT_REQUEST,
    });
    if (agoraEngine) {
      await agoraEngine.leaveChannel();
    }
    await tryDestroyAgoraInstance();
    dispatch(callDisconnected());
  } catch (e) {
    Logger.notify(e);
    throw e;
  }
};

export const callDisconnected = (): ICallConnectionAction => {
  try {
    return {
      type: CALL_DISCONNECTED,
      // ...analyicsForAction(CALL_DISCONNECTED),
    };
  } catch (e) {
    Logger.notify(e);
    throw e;
  }
};

export const participantLeftCall = (uid: number, userIdString: string): IParticipentEvent => ({
  type: PARTICIPANT_LEFT_CALL,
  payload: {
    uid,
    userIdString,
  },
});

export const participantJoinedCall = (
  uid: number,
  userIdString?: string,
): IParticipentEvent => ({
  type: PARTICIPANT_JOINED_CALL,
  payload: {
    uid,
    userIdString,
  },
});

export const toggleLocalAudioMuted = () => async (
  dispatch: (action: IMuteLocalAudioEvent) => void,
  getState: () => IAppStore,
) => {
  const currentState = getState();
  dispatch({
    type: TOGGLE_LOCAL_AUDIO_MUTED,
    ...analyicsForAction(currentState.groupCalling.localAudioIsMuted
      ? 'workout_video_call_unmute'
      : 'workout_video_call_mute'),
  });

  const {
    groupCalling: {
      localAudioIsMuted,
    },
  } = getState();
  if (agoraEngine) {
    await agoraEngine.muteLocalAudioStream(localAudioIsMuted);
  }
};

export const enableTalking = () => async (
  dispatch: (action: IToggleTalkingEnabledEvent) => void,
  getState: () => IAppStore,
) => {
  await enableAgoraAudio(dispatch, getState());
};

export const enableAgoraAudio = async (dispatch: any, state: IAppStore) => {
  const {
    groupCalling: {
      settings,
      connectionState,
    },
  } = state;

  if (!agoraEngine) {
    return;
  }

  const agoraIsNotConnected = connectionState !== CallConnectionState.connected || !settings;
  if (agoraIsNotConnected) {
    return;
  }

  dispatch({
    type: TALKING_TOGGLE_REQUEST,
  });
  await enableAllAudioAndSetUpProfile(settings);
  dispatch({
    type: ENABLE_TALKING_SUCCESS,
    ...analyicsForAction(ENABLE_TALKING_SUCCESS),
  });
};

export const disableTalking = () => async (
  dispatch: (action: IToggleTalkingEnabledEvent) => void,
  getState: () => IAppStore,
) => {
  await disableAgoraAudio(getState(), dispatch);
};

export const disableAgoraAudio = async (
  state: IAppStore,
  dispatch: (action: IToggleTalkingEnabledEvent) => void,
) => {
  if (!agoraEngine) {
    return;
  }

  dispatch({
    type: TALKING_TOGGLE_REQUEST,
  });
  await disableAllAudioAndResetAudioMode();

  dispatch({
    type: DISABLE_TALKING_SUCCESS,
    ...analyicsForAction(DISABLE_TALKING_SUCCESS),
  });
};

export const toggleLocalVideoMuted = () => async (
  dispatch: (action: IMuteLocalVideoEvent) => void,
  getState: () => IAppStore,
) => {
  const currentState = getState();
  dispatch({
    type: TOGGLE_LOCAL_VIDEO_MUTED,
    ...analyicsForAction(currentState.groupCalling.localVideoIsMuted
      ? 'workout_video_call_camera_on'
      : 'workout_video_call_camera_off'),
  });

  const {
    groupCalling: {
      localVideoIsMuted,
    },
  } = getState();
  if (agoraEngine) {
    await agoraEngine.enableLocalVideo(!localVideoIsMuted);
  }
};

const initializeAgoraEngine = async (
  accessToken: string,
  appId: string,
  channelName: string,
  localUserAccount: string,
  settings: IAgoraSettings,
  dispatch: (action: VideoCallingActionTypes | IVolumeChangedAction) => void,
  userProfile: UserProfile,
  storeState: IAppStore,
) => {
  if (agoraEngine) {
    await tryDestroyAgoraInstance();
  }
  agoraEngine = createAgoraRtcEngine();
  agoraEngine.initialize({ appId });

  const dimensions = new VideoDimensions();
  dimensions.height = settings.videoDimensions.height;
  dimensions.width = settings.videoDimensions.width;
  const mirrorMode = build.variant.isElite
    ? VideoMirrorModeType.VideoMirrorModeEnabled
    : VideoMirrorModeType.VideoMirrorModeAuto;
  const config = {
    dimensions,
    mirrorMode,
  };
  Logger.debug('Agora: setup', { settings, config });
  await agoraEngine.setVideoEncoderConfiguration(config);

  if (Platform.OS === 'ios') {
    const deactivateSession = AudioSessionOperationRestriction.AudioSessionOperationRestrictionDeactivateSession;
    await agoraEngine.setAudioSessionOperationRestriction(deactivateSession);
  }

  const warningHandler = (warn) => {
    Logger.leaveBreadcrumb('Agora: Warning', warn);
  };

  const onError = (code, errorMessage: string) => {
    dispatch({
      type: CALL_FAILED,
      payload: {
        errorMessage,
      },
    });
    Logger.notify(`Agora Error: ${code} ${errorMessage}`);
  };

  const onUserJoined = userJoined(dispatch);
  const onUserInfoUpdated = userUpdated(dispatch);

  const onUserOffline = async (connection, uid: number, reason) => {
    try {
      const remoteUserInfo = await getUserAccountFrom(uid);
      if (remoteUserInfo?.userAccount) {
        dispatch(participantLeftCall(uid, remoteUserInfo.userAccount));
      }
    } catch (e) {
      Logger.notify(e);
    }
  };

  const onUserEnableLocalVideo = async (connection, uid: number, enabled: boolean) => {
    dispatch({
      type: USER_TOGGLE_VIDEO,
      payload: { uid, enabled },
    });
  };

  const onConnectionStateChanged = async (connection, state: ConnectionStateType) => {
    if (state === ConnectionStateType.ConnectionStateDisconnected) {
      dispatch(callDisconnected());
    }

    if (state === ConnectionStateType.ConnectionStateConnected) {
      if (!agoraEngine) {
        return;
      }

      if (storeState.homeWorkouts.warmupOrFirstExerciseHasStartedPlaying
          && storeState.homeWorkouts.homeWorkoutSession
          && !storeState.homeWorkouts.homeWorkoutSession.isStreamingWorkout) {
        const appConfig = getConfig();
        await sleep(appConfig.groupWorkoutVideoCallStartDelayInMs);
        await disableAgoraAudio(storeState, dispatch);
      }
      dispatch(callConnected());
    }
  };

  const onAudioVolumeIndication = (connection, speakers: AudioVolumeInfo[] | undefined) => {
    if (!speakers || speakers.length === 0) {
      return;
    }
    const speakerVolumeInfo = speakers.find((speaker: AudioVolumeInfo) => speaker.uid === 0);
    if (!speakerVolumeInfo) {
      return;
    }
    dispatch(myTalkingVolumeChanged(speakerVolumeInfo));
  };

  agoraEngine.registerEventHandler({
    onError,
    onUserJoined,
    onUserInfoUpdated,
    onUserOffline,
    onUserEnableLocalVideo,
    onConnectionStateChanged,
    onAudioVolumeIndication,

    onLocalAudioStateChanged: (connection, state, error) => {
      if (state === LocalAudioStreamState.LocalAudioStreamStateFailed) {
        warningHandler(error);
      }
    },
    onLocalVideoStateChanged: (source, state, error) => {
      if (state === LocalVideoStreamState.LocalVideoStreamStateFailed) {
        warningHandler(error);
      }
    },
  });

  function determineClientRoleType(): ClientRoleType {
    if (settings.isStreamingWorkout && !settings.isHost) {
      return ClientRoleType.ClientRoleAudience;
    }

    return ClientRoleType.ClientRoleBroadcaster;
  }

  if (settings.isStreamingWorkout) {
    const appConfig = getConfig();
    await agoraEngine.setChannelProfile(ChannelProfileType.ChannelProfileLiveBroadcasting);

    agoraEngine.setAudioProfile(appConfig.agoraStreamingAudioProfile);
    agoraEngine.setAudioScenario(appConfig.agoraStreamingAudioScenario);

    if (!settings.isHost) {
      dispatch(beepVolumeChanged(appConfig.streamingWorkoutBeepAndVoiceVolume));
      dispatch(voiceVolumeChanged(appConfig.streamingWorkoutBeepAndVoiceVolume));
    } else {
      const volumeIndicationIntervalMs = 1000;
      const smoothingFactor = 3;
      const reportActiveSpeaker = true;
      await agoraEngine.enableAudioVolumeIndication(
        volumeIndicationIntervalMs,
        smoothingFactor,
        reportActiveSpeaker,
      );
    }
  }
  await agoraEngine.enableVideo();
  await agoraEngine.joinChannelWithUserAccount(
    accessToken,
    channelName,
    localUserAccount,
    {
      autoSubscribeAudio: true,
      autoSubscribeVideo: true,
      clientRoleType: determineClientRoleType(),
    },
  );
};

const getUserAccountFrom = async (uid: number): Promise<UserInfo | undefined> => {
  if (!agoraEngine) {
    return;
  }
  return agoraEngine.getUserInfoByUid(uid);
};

const tryDestroyAgoraInstance = async () => {
  try {
    if (!agoraEngine) {
      return;
    }
    agoraEngine.release(/* sync (suggested) */ true);
    agoraEngine = null;
  } catch (e) {
    Logger.notify('failed to destroy agora instance', e);
  }
};

const myTalkingVolumeChanged = (speakerVolumeInfo: AudioVolumeInfo): IMyTalkingVolumeChangedAction => ({
  type: SPEAKER_VOLUME_CHHANGED,
  payload: {
    speakerVolumeInfo,
  },
});

interface IMyTalkingVolumeChangedAction {
  type: typeof SPEAKER_VOLUME_CHHANGED;
  payload: {
    speakerVolumeInfo: AudioVolumeInfo;
  };
}
interface ICallConnectionAction {
  type: typeof CALL_DISCONNECTED;
}

interface IFailedToConnectAction {
  type: typeof CALL_FAILED;
  payload: {
    errorMessage: string,
    permissionError?: boolean;
  };
}

interface IInitiateCallAction {
  type: typeof CALL_INITIATING;
  payload: {
    userProfileId: string;
    isStreamingWorkoutAndNotHost: boolean;
  };
}

interface IIniatiatePayload {
  accessToken: string;
  settings: any;
}
interface IInitiateGroupCallSuccess {
  type: typeof CALL_INITIATE_SUCCESS;
  payload: IIniatiatePayload;
}

interface IParticipentEvent {
  type: typeof PARTICIPANT_JOINED_CALL |
  typeof PARTICIPANT_LEFT_CALL;
  payload: {
    uid: number,
    userIdString?: string,
  };
}

interface ICallDisconnectRequestAction {
  type: typeof CALL_DISCONNECT_REQUEST;
}

// todo: move to models
interface IAgoraSettings {
  videoDimensions: {
    width: number,
    height: number,
  };
  frameRate: number;
  audioScenario: number;
  audioProfile: number;
  channelProfile: number;
  role: number;
  isStreamingWorkout: boolean;
  isHost: boolean;
}

interface IMuteLocalAudioEvent {
  type: typeof TOGGLE_LOCAL_AUDIO_MUTED;
}

interface IMuteLocalVideoEvent {
  type: typeof TOGGLE_LOCAL_VIDEO_MUTED;
}

interface IClassListLoadedEvent {
  type: typeof CLASS_LIST_LOADED;
  payload: {
    classList: IDisplayableUserProfile[],
  };
}

interface IUserToggleVideoEvent {
  type: typeof USER_TOGGLE_VIDEO;
  payload: {
    uid: number;
    enabled: boolean;
  };
}

interface IToggleTalkingEnabledEvent {
  type: typeof DISABLE_TALKING_SUCCESS
  | typeof ENABLE_TALKING_SUCCESS
  | typeof TALKING_TOGGLE_REQUEST;
}

export type VideoCallingActionTypes =
  IFailedToConnectAction |
  ICallConnectionAction |
  IInitiateGroupCallSuccess |
  IInitiateCallAction |
  ICallDisconnectRequestAction |
  IMuteLocalAudioEvent |
  IMuteLocalVideoEvent |
  IParticipentEvent |
  IToggleTalkingEnabledEvent |
  IUserToggleVideoEvent |
  IClassListLoadedEvent |
  IMyTalkingVolumeChangedAction |
  ICallConnectedAction;

async function disableAllAudioAndResetAudioMode() {
  if (!agoraEngine) {
    return;
  }

  if (Platform.OS !== 'android') {
    await agoraEngine.disableAudio();
  }
  await agoraEngine.muteAllRemoteAudioStreams(true);
  await agoraEngine.muteLocalAudioStream(true);
  await sleep(1000);
  await AudioHelper.setupAudioMode();
  await sleep(1000);
}

async function enableAllAudioAndSetUpProfile(settings: any) {
  if (!agoraEngine) {
    return;
  }
  if (Platform.OS !== 'android') {
    await agoraEngine.enableAudio();
  }
  await agoraEngine.setAudioProfile(
    settings.audioProfile,
    settings.audioScenario,
  );
  await agoraEngine.muteLocalAudioStream(false);
  await agoraEngine.muteAllRemoteAudioStreams(false);
  await sleep(1000);
}

function userJoined(dispatch: (action: VideoCallingActionTypes | IVolumeChangedAction) => void) {
  return async (connection, uid) => {
    try {
      const remoteUserInfo = await getUserAccountFrom(uid);
      if (remoteUserInfo && remoteUserInfo.userAccount) {
        dispatch(participantJoinedCall(uid, remoteUserInfo.userAccount));
      }
    } catch (e) {
      Logger.notify(e);
    }
  };
}

function userUpdated(dispatch: (action: VideoCallingActionTypes | IVolumeChangedAction) => void) {
  return async (uid) => {
    try {
      const remoteUserInfo = await getUserAccountFrom(uid);
      if (remoteUserInfo && remoteUserInfo.userAccount) {
        dispatch(participantJoinedCall(uid, remoteUserInfo.userAccount));
      }
    } catch (e) {
      Logger.notify(e);
    }
  };
}
